<template>
    <div>
        <h1 class="font-walsheim text-lg mb-6">Contact sales</h1>
        <p class="text-sm mb-6" v-if="!$store.getters.contactedSales">If you're interested in a monthly plan, simply fill out the form. One of our small business consultants will then give you a call to create a plan for your business.</p>
        <form class="w-full mb-6" @submit.prevent v-if="!$store.getters.isSubmittingContactSales && !$store.getters.contactedSales">
            <div class="field-grid">
                <InputField
                    key="firstName"
                    v-model="firstName"
                    :value="firstName"
                    data-qa="firstName-field"
                    :valid="firstNameValid"
                    field-label="First name"
                    field-type="text"
                    required
                    :readonly="$store.getters.isUpgrading"
                    class="col-span-2"
                >
                    <template v-slot:error>
                        <p v-text="firstNameError"></p>
                    </template>
                </InputField>
                <InputField
                    key="lastName"
                    v-model="lastName"
                    :value="lastName"
                    data-qa="lastName-field"
                    :valid="lastNameValid"
                    field-label="Last name"
                    field-type="text"
                    required
                    :readonly="$store.getters.isUpgrading"
                    class="col-span-2"
                >
                    <template v-slot:error>
                        <p v-text="lastNameError"></p>
                    </template>
                </InputField>
                <InputField
                    key="email"
                    v-model="email"
                    :value="email"
                    data-qa="email-field"
                    :valid="emailValid"
                    field-label="Email"
                    field-type="text"
                    required
                    :readonly="$store.getters.isUpgrading"
                    class="col-span-2"
                >
                    <template v-slot:error>
                        <p v-text="emailError"></p>
                    </template>
                </InputField>
                <PhoneField
                    key="phone"
                    v-model="phone"
                    :value="phone"
                    data-qa="phone-field"
                    :valid="phoneValid"
                    field-label="Phone"
                    field-type="text"
                    required
                    :readonly="$store.getters.isUpgrading && hasPhoneNumber"
                    class="col-span-2"
                >
                    <template v-slot:error>
                        <p v-text="phoneError"></p>
                    </template>
                </PhoneField>
                <div class="col-span-2">
                    <KeapButton @click.prevent="submitContactSales()" small-expanded data-qa="contact-sales-button" :disabled="!canContactSales || $store.getters.isSubmittingContactSales || $store.getters.contactedSales">
                        Contact sales
                    </KeapButton>
                </div>
            </div>
        </form>
        <Spinner class="w-[80px] h-[80px] mx-auto mb-6" v-if="$store.getters.isSubmittingContactSales"/>
        <p class="text-sm mb-6" v-if="$store.getters.contactedSales">Thank you! One of our small business consultants will contact you by the end of the business day.</p>
        <p class="text-sm">Can’t wait? Call <a class="underline" href="tel:{{ supportNumber }}">{{ supportNumberDisplay }}</a> to speak to a small business consultant now.</p>

        <Modal ref="learnMoreModal" :qa-name="`coaching_learnMore_modal`">
            <h1 class="text-xl font-bold mb-3">Let's Grow Guarantee</h1>
            <p class="mb-4 last:mb-0">Within the first 90 days of using Keap, you will achieve your main growth goal or we will keep coaching you until your goal is met.</p>
            <p class="mb-4 last:mb-0">The goal will be determined by you and your coach during your first meeting. It will be specific to your business and reasonable to achieve within the 90-day timeframe. You must attend all of your coaching sessions, complete your assigned action items, and actively contribute to your own success, and your coach will do the same.</p>
            <p class="mb-4 last:mb-0">Call us at <a href="tel:18668000004,1" class="underline">+1 866-800-0004 x1</a> for more details.</p>
            <p class="mb-4 last:mb-0">You’ll also receive up to 15 hours of free migration services, where we take critical marketing assets from your previous platform and recreate them in Keap. This includes email templates, landing pages, web forms, automations and sales pipeline.</p>
        </Modal>
    </div>
</template>

<script>
import Modal from '@/Components/Modal.vue';
import InputField from '@/BuyNow/Components/inputs/InputField.vue';
import PhoneField from '@/BuyNow/Components/inputs/PhoneField.vue';
import KeapButton from '@/Components/KeapButton.vue';
import Spinner from '@/BuyNow/Components/Spinner.vue';
import * as Analytics from '@/Util/reporting.js';
import { EMAIL_REGEX, PHONE_REGEX, SUPPORT_PHONE_NUMBER } from '@/Util/constants.js';

export default {
    components: {
        Modal,
        PhoneField,
        InputField,
        Spinner,
        KeapButton,
    },
    data() {
        return {
            hasPhoneNumber: true,
        };
    },
    methods: {
        async submitContactSales() {
            window.Sentry.setUser(this.$store.getters.userInfo.email);

            Analytics.pushToDataLayer({
                event: 'click',
                attributes: {
                    section: 'Plan Overview',
                    action: 'Click button',
                    event_label: 'Contact Sales',
                },
            });

            this.$store.commit('setSubmittingContactSales', true);
            await this.$store.dispatch('submitContactSales');
        },
    },
    mounted() {
        if (!this.$store.getters.userInfo.phone) {
            this.hasPhoneNumber = false;
        }
    },
    updated() {
    },
    computed: {
        canContactSales() {
            return !this.$store.getters.isSoftLoading && this.$store.getters.canContactSales;
        },
        firstName: {
            get() {
                return this.$store.state.userInfo.firstName;
            },
            set(value) {
                this.$store.commit('updateUserInfo', { firstName: value });
            }
        },
        firstNameValid: {
            get() {
                return this.$store.state.validation.firstName.valid;
            },
            set(value) {;
                this.$store.commit('updateValidation', { firstName: { valid: value, message: this.firstNameError }});
            }
        },
        firstNameError: {
            get() {
                return this.$store.state.validation.firstName.message;
            },
            set(value) {
                this.$store.commit('updateValidation', { firstName: { valid: this.firstNameValid, message: value }});
            }
        },
        lastName: {
            get() {
                return this.$store.state.userInfo.lastName;
            },
            set(value) {
                this.$store.commit('updateUserInfo', { lastName: value });
            }
        },
        lastNameValid: {
            get() {
                return this.$store.state.validation.lastName.valid;
            },
            set(value) {
                this.$store.commit('updateValidation', { lastName: { valid: value, message: this.lastNameError }});
            }
        },
        lastNameError: {
            get() {
                return this.$store.state.validation.lastName.message;
            },
            set(value) {
                this.$store.commit('updateValidation', { lastName: { valid: this.lastNameValid, message: value }});
            }
        },
        email: {
            get() {
                return this.$store.state.userInfo.email;
            },
            set(value) {
                this.$store.commit('updateUserInfo', { email: value });
            }
        },
        emailValid: {
            get() {
                return this.$store.state.validation.email.valid;
            },
            set(value) {
                this.$store.commit('updateValidation', { email: { valid: value, message: this.emailError }});
            }
        },
        emailError: {
            get() {
                return this.$store.state.validation.email.message;
            },
            set(value) {
                this.$store.commit('updateValidation', { email: { valid: this.emailValid, message: value }});
            }
        },
        phone: {
            get() {
                return this.$store.state.userInfo.phone;
            },
            set(value) {
                this.$store.commit('updateUserInfo', { phone: value });
            }
        },
        phoneValid: {
            get() {
                return this.$store.state.validation.phone.valid;
            },
            set(value) {
                this.$store.commit('updateValidation', { phone: { valid: value, message: this.phoneError }});
            }
        },
        phoneError: {
            get() {
                return this.$store.state.validation.phone.message;
            },
            set(value) {
                this.$store.commit('updateValidation', { phone: { valid: this.phoneValid, message: value }});
            }
        },
        supportNumber() {
            return `tel:${SUPPORT_PHONE_NUMBER},1`;
        },
        supportNumberDisplay() {
            return `${SUPPORT_PHONE_NUMBER} ext. 1`;
        },
    },
    watch: {
        // Form validation
        firstName(newVal, oldVal) {
            if (newVal == '') {
                this.firstNameValid = false;
                this.firstNameError = 'First name is required';
            } else {
                this.firstNameValid = true;
            }
        },
        lastName(newVal, oldVal) {
            if (newVal == '') {
                this.lastNameValid = false;
                this.lastNameError = 'Last name is required';
            } else {
                this.lastNameValid = true;
            }
        },
        email(newVal, oldVal) {
            if (newVal == '') {
                this.emailValid = false;
                this.emailError = 'Email is required';
            } else if (!RegExp(EMAIL_REGEX).test(newVal)) {
                this.emailValid = false;
                this.emailError = 'Email is not valid';
            } else {
                this.emailValid = true;
            }
        },
        phone(newVal, oldVal) {
            if (newVal == '') {
                this.phoneValid = false;
                this.phoneError = 'Phone is required';
            } else if (!RegExp(PHONE_REGEX).test(newVal)) {
                this.phoneValid = false;
                this.phoneError = 'Phone is not valid';
            } else {
                this.phoneValid = true;
            }
        },
    }
};
</script>

<style lang="postcss" scoped>
</style>
